import { Component, OnInit, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { BaseDialogComponent } from '../../forms/base-dialog/base-dialog.component';
import { BaseEnrollmentComponent } from '../base-enrollment/base-enrollment.component';
import { environment } from 'src/environments/environment';
import { FormBuilder } from '@angular/forms';
import { forkJoin, from, mergeMap } from 'rxjs';

@Component({
  selector: 'app-signature-enrollment',
  templateUrl: './signature-enrollment.component.html',
  styleUrls: ['./signature-enrollment.component.css']
})
export class SignatureEnrollmentComponent extends BaseEnrollmentComponent implements OnInit, AfterViewInit {
  @ViewChild('SignatureStudent') override signatureStudent: ElementRef<HTMLCanvasElement>;
  @ViewChild('SignatureStudio') override signatureStudio: ElementRef<HTMLCanvasElement>;
  @ViewChild('SignatureParent') override signatureParent: ElementRef<HTMLCanvasElement>;

  // Add the proper entity type and bundle
  override eckType = "packages";
  override bundle = "enrollment";
  override entityId = this._injectedDialogData['data']['EntityID'];

  override ngOnInit(): void {
    super.ngOnInit();

    // The agreement templates need to know whether the saved field_taxes is
    // inside the price (inclusive) or added on top (exclusive, e.g. Canadian GST).
    this.loadTaxInclusiveFlag();

    // Initialize tosAccepted based on entity data if available
    this._entityRESTService.getEntity(this.eckType, this.bundle, this.entityId).subscribe(
      (data: any) => {
        if (data && data.field_tos_accepted_date) {
          this.tosAccepted = true;
        }
      }
    );

    // Load the agreement template key from the enrollment's agreement reference or studio default
    this._drupalRESTService.httpGET('/api_rest/v1/loadEnrollmentAgreements')
      .subscribe((agreements: any[]) => {
        if (!agreements || agreements.length === 0) return;
        const defaultAgreement = agreements.find(a => a.field_is_default?.[0]?.value === '1');
        const selected = defaultAgreement || agreements[0];
        if (selected?.field_template_key?.[0]?.value) {
          this.activeTemplateKey = selected.field_template_key[0].value;
          // The back page depends on the template key. Regenerate now that it's known.
          this.generateBackpageDisplayContent();
        }
      });
  }

  override ngAfterViewInit(): void {
    super.ngAfterViewInit();

    // Initialize tosAccepted based on entity data
    if (this.entityData?.field_tos_accepted_date) {
      this.tosAccepted = true;
    } else if (this.enrollmentForm?.get('field_tos_accepted_date')?.value) {
      this.tosAccepted = true;
    } else {
      this.tosAccepted = false;
    }

    // Make sure to initialize signature canvases after the view is initialized
    setTimeout(() => {
      this.initializeSignatureCanvases();
    }, 0);
  }

  /**
   * Initialize all signature canvases with proper event handlers for all devices
   */
  private initializeSignatureCanvases(): void {
    if (this.signatureStudent && this.signatureStudent.nativeElement) {
      this.signatureInit(this.signatureStudent, 'student');
    }

    if (this.signatureStudio && this.signatureStudio.nativeElement) {
      this.signatureInit(this.signatureStudio, 'studio');
    }

    // Only initialize parent signature if it's visible (previously signed or shown)
    if (this.signatureParent && this.signatureParent.nativeElement &&
        (this.detectSignatureParent || this._enrollmentEntityData?.['field_parent_guardian_signature']?.[0]?.['url'])) {
      this.signatureInit(this.signatureParent, 'parent');
    }
  }

  /**
   * Override onSubmitSignatures to ensure signatures are properly detected and TOS is accepted
   */
  override async onSubmitSignatures(action: string): Promise<void> {
    // Make sure parent signature is properly detected before submitting
    if (this.signatureParent && this.signatureParent.nativeElement) {
      this.checkParentSignature(this.signatureParent.nativeElement);
    }

    // Check if Terms of Service is accepted
    if (!this.tosAccepted) {
      this._snackBar.open('Please accept the Terms of Service before proceeding', 'Close', {
        duration: 3000,
      });
      return;
    }

    // Call the parent implementation
    await super.onSubmitSignatures(action);
  }

  /**
   * Show the parent signature area
   */
  showParentSignature() {
    this.detectSignatureParent = true;
    // Initialize the parent signature canvas after making it visible
    setTimeout(() => {
      if (this.signatureParent) {
        this.signatureInit(this.signatureParent, 'parent');
      }
    }, 0);
  }
}
